/**
 * 機能の ON/OFF（2026-09-27 task/toggles で表示、creator/dashboard-review で画面からの切り替えを追加）。概要ページに置く。
 *
 * 正は手元の kumo.config.json（`xkumo config push` はファイルの内容で上書きする）。画面で切り替えると、手元に同じ変更を
 * 入れない限り次の push で黙って戻る。だから切り替えは:
 *   - 必ず確認ダイアログ（何が ON / OFF になるか・依存で一緒に変わるもの・どの環境か）。本番はプロジェクトの slug を打つ強い確認
 *   - 反映は設定のページと同じ PUT …/config（サーバーの検証・監査ログをそのまま使う。切り替え専用の API は作らない）
 *   - 反映したら手元に入れるコマンド（`xkumo features on …`）を出し、ブラウザに記録して「まだ手元に無いかも / push で戻った」を知らせる
 *   - 公開中のサイトを止める OFF（hosting 等・本体の 409 would_stop_serving・AGT-16 2026-09-29）は、同じダイアログで「止まります（URL）」を
 *     赤で出して確かめ、もう一度押したら `?allowTurnOff=` を付けて送り直す（featureToggle.ts stopServingAsk）
 * 判断（依存・書き換え・食い違い）は featureToggle.ts（テスト付き）。
 *
 * ON の機能を上に並べる（「このゲームが何を使っているか」を最初に見せる）。OFF は既定の状態なので控えめに。
 * スマホ幅でも横スクロールにならないよう、表ではなく折り返す行（.feature-row）で並べる。
 *
 * 最初の公開で見せない機能（launchHidden.ts・founder 2026-09-27 MASTER-PLAN §1c）は行に出さず、数にも入れない。
 * ただし依存の判断（planToggle）と食い違いの検出（driftOf）はサーバーの全部の一覧で行う: 例えば古い config で leaderboards が
 * ON のまま stats を OFF にすると、隠れた leaderboards も一緒に OFF にしないと push が拒否される（確認ダイアログには出る = 黙らない）。
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, envPath, type ApiError, type FeaturesView, type Issue } from '../api';
import { applyChanges, configPutPath, driftOf, localFix, planToggle, stopServingAsk, strongConfirmOk, toggleKind, type Change, type ToggleRecord } from '../featureToggle';
import { isLaunchHidden } from '../launchHidden';
import { useResource } from '../hooks';
import { errorText, translate, useT, type MessageKey } from '../i18n';
import { en } from '../i18n/en';
import { CLI_NAME } from '../cliCommand';
import { CodeBlock, ConfirmDialog, ErrorState, Spinner } from './ui';

type Env = 'development' | 'production';

// この画面で切り替えた記録（ブラウザごと）。サーバーに持たないのは「手元のファイルに入れたか」をサーバーは知り得ないため。
// 消えても（別のブラウザ・プライベートモード）知らせが出ないだけで、切り替え自体は壊れない
const recordKey = (projectId: string, env: Env) => `kumo.console.featureToggles.${projectId}.${env}`;
function readRecord(projectId: string, env: Env): ToggleRecord | null {
  try {
    const raw = localStorage.getItem(recordKey(projectId, env));
    return raw ? (JSON.parse(raw) as ToggleRecord) : null;
  } catch {
    return null;
  }
}
function writeRecord(projectId: string, env: Env, rec: ToggleRecord | null) {
  try {
    if (rec) localStorage.setItem(recordKey(projectId, env), JSON.stringify(rec));
    else localStorage.removeItem(recordKey(projectId, env));
  } catch {
    /* 保存できないブラウザでは知らせを出さないだけ */
  }
}

/** 機能の表示名（表示中の言語）。辞書に無い新しい機能はサーバーの英語の title を出す（一覧の正はサーバーなので、足し忘れても壊れない） */
export function featureTitle(key: string, fallback: string): string {
  const k = `feature.${key}` as MessageKey;
  return k in en ? translate(k) : fallback;
}

export function FeaturesCard({ projectId, slug, env }: { projectId: string; slug: string; env: Env }) {
  const base = envPath(projectId, env);
  const r = useResource<FeaturesView>(`${base}/features`, 30_000);
  const { t, tr } = useT();
  // list = サーバーの全部（依存の判断用）/ shown = 画面に並べる分（最初の公開で見せない機能を除く）
  const list = r.data?.features ?? [];
  const shown = list.filter((f) => !isLaunchHidden(f.key));
  const on = shown.filter((f) => f.enabled);
  const off = shown.filter((f) => !f.enabled);

  const [record, setRecord] = useState<ToggleRecord | null>(() => readRecord(projectId, env));
  const [plan, setPlan] = useState<Change[] | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; issues: Issue[] } | null>(null);
  const [done, setDone] = useState<{ changes: Change[]; version: number; changed: boolean } | null>(null);
  // 409 would_stop_serving の後の「止まります（URL）」の確かめ（featureToggle.ts stopServingAsk・AGT-16）。ある間は次の反映に allowTurnOff を付ける
  const [stopAsk, setStopAsk] = useState<{ features: string[]; liveUrl: string | null } | null>(null);

  // 環境・プロジェクトを切り替えたら、その組の記録に差し替える（前の環境の知らせを出さない）
  useEffect(() => {
    setRecord(readRecord(projectId, env));
    setDone(null);
  }, [projectId, env]);

  const drift = r.data ? driftOf(record, r.data.version, list) : { kind: 'none' as const, changes: [] };
  // その後の push でも画面の変更が残っている = 手元にも入った。記録はもう要らない
  useEffect(() => {
    if (r.data && record && drift.kind === 'none') {
      writeRecord(projectId, env, null);
      setRecord(null);
    }
  }, [r.data, record, drift.kind, projectId, env]);

  const strong = env === 'production';
  const open = (key: string, next: boolean) => {
    setPlan(planToggle(list, key, next));
    setTyped('');
    setErr(null);
    setDone(null);
    setStopAsk(null);
  };
  const close = () => {
    if (!busy) setPlan(null);
  };

  const apply = async () => {
    if (!plan || !r.data) return;
    setBusy(true);
    setErr(null);
    try {
      // 反映の直前に今の config を取り直す（画面を開いた後に CLI で push されていたら、その内容の上に重ねる）
      const cur = await api<{ version: number; config: unknown }>(`${base}/config`);
      if (cur.version !== r.data.version) {
        // 一覧が古い = 依存の判断も古いかもしれない。黙って進めず、最新の一覧で選び直してもらう
        setErr({ text: t('features.stale', { version: String(cur.version) }), issues: [] });
        r.reload();
        return;
      }
      const res = await api<{ version: number; changed: boolean }>(configPutPath(base, stopAsk?.features ?? null), { method: 'PUT', body: applyChanges(cur.config, plan) });
      if (res.changed) {
        // 前の記録がまだ手元に入っていない（同じ版から続けて変えた）なら、キーごとに新しい方で合わせて 1 つにする
        const prev = record && record.version === cur.version ? record.changes : [];
        const merged = [...prev.filter((p) => !plan.some((c) => c.key === p.key)), ...plan];
        const rec: ToggleRecord = { version: res.version, at: new Date().toISOString(), changes: merged };
        writeRecord(projectId, env, rec);
        setRecord(rec);
      }
      setDone({ changes: plan, version: res.version, changed: res.changed });
      setPlan(null);
      setStopAsk(null);
      r.reload();
    } catch (e) {
      const ae = e as ApiError;
      // 公開中のサイトが止まる = 何も変わっていない。ダイアログを開いたまま「止まります（URL）」を出し、もう一度押したら allowTurnOff を付ける
      const ask = !stopAsk && ae.code === 'would_stop_serving' ? stopServingAsk(plan, ae.details) : null;
      if (ask) {
        setStopAsk(ask);
        return;
      }
      setErr({ text: errorText(ae), issues: ae.issues });
    } finally {
      setBusy(false);
    }
  };

  const envName = t(`env.inline.${env}`);
  const self = plan?.[0];

  return (
    <section className="card" aria-label={t('features.title')}>
      <div className="card-head">
        <h2 className="card-title">{t('features.title')}</h2>
        <span className="muted small">{t('features.count', { on: on.length, total: shown.length })}</span>
      </div>
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('features.title')} onRetry={r.reload} />}
      {r.data && (
        <>
          <p className="muted small">{tr('features.body', { cmd: <code>{CLI_NAME} features on &lt;name&gt;</code>, push: <code>{CLI_NAME} config push</code> })}</p>

          {done && (
            <div className="alert alert-ok" role="status">
              <strong>{done.changed ? t('features.doneTitle', { version: String(done.version) }) : t('features.doneSame')}</strong>
              {done.changed && <LocalFix changes={done.changes} env={env} />}
            </div>
          )}
          {!done && drift.kind !== 'none' && (
            <div className={`alert ${drift.kind === 'reverted' ? 'alert-warn' : 'alert-info'}`} role="status">
              <strong>
                {drift.kind === 'reverted'
                  ? t('features.revertedTitle', { names: drift.changes.map((c) => c.key).join(', ') })
                  : t('features.pendingTitle', { names: drift.changes.map((c) => c.key).join(', ') })}
              </strong>
              <span>{drift.kind === 'reverted' ? t('features.revertedBody') : t('features.pendingBody')}</span>
              <LocalFix changes={drift.changes} env={env} />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  writeRecord(projectId, env, null);
                  setRecord(null);
                }}
              >
                {t('features.dismiss')}
              </button>
            </div>
          )}

          {on.length === 0 && <p>{t('features.none')}</p>}
          <ul className="feature-list">
            {[...on, ...off].map((f) => {
              const kind = toggleKind(f);
              return (
                <li key={f.key} className="feature-row">
                  <span className={`pill ${f.enabled ? 'pill-ok' : ''}`}>{f.enabled ? t('features.on') : t('features.off')}</span>
                  <div className="feature-name">
                    <strong>{f.key}</strong>
                    <div className="muted small">{featureTitle(f.key, f.title)}</div>
                    <code className="small feature-path">{f.configPath}</code>
                  </div>
                  <div className="feature-action">
                    {kind === 'toggle' && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(f.key, !f.enabled)}>
                        {f.enabled ? t('features.turnOff') : t('features.turnOn')}
                      </button>
                    )}
                    {kind === 'signIn' && (
                      <Link className="btn btn-ghost btn-sm" to="../sign-in">
                        {t('features.setUpSignIn')}
                      </Link>
                    )}
                    {kind === 'functions' && (
                      <Link className="btn btn-ghost btn-sm" to="../functions">
                        {t('features.openFunctions')}
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <ConfirmDialog
        open={!!plan}
        title={self ? t(self.on ? 'features.confirmOnTitle' : 'features.confirmOffTitle', { name: self.key, env: envName }) : t('features.title')}
        confirmLabel={stopAsk ? t('features.confirmStop') : self?.on ? t('features.confirmOn', { env: envName }) : t('features.confirmOff', { env: envName })}
        // OFF は動いているゲームの機能を止める（プレイヤーに 403）、本番はプレイヤーに届く = どちらも赤で Cancel にフォーカス
        tone={strong || (self && !self.on) ? 'danger' : 'default'}
        busy={busy}
        confirmDisabled={strong && !strongConfirmOk(typed, slug)}
        onClose={close}
        onConfirm={() => void apply()}
      >
        {plan && self && (
          <>
            <p>{t(self.on ? 'features.confirmOnBody' : 'features.confirmOffBody', { env: envName })}</p>
            <ul className="feature-changes">
              {plan.map((c) => (
                <li key={c.key}>
                  <strong>{c.key}</strong> <span className="muted">{c.on ? 'OFF → ON' : 'ON → OFF'}</span> <code className="small">{c.configPath}</code>
                </li>
              ))}
            </ul>
            {plan.length > 1 && <p className="small">{t(self.on ? 'features.alsoOn' : 'features.alsoOff')}</p>}
            <p className="small muted">{tr('features.localNote', { file: <code>kumo.config.json</code>, push: <code>{CLI_NAME} config push</code> })}</p>
            {strong && (
              <label className="field">
                <span>{tr('features.typeSlug', { slug: <code>{slug}</code> })}</span>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-invalid={typed.length > 0 && !strongConfirmOk(typed, slug)}
                />
              </label>
            )}
          </>
        )}
        {stopAsk && (
          <p className="alert alert-error" role="alert">
            {stopAsk.liveUrl
              ? tr('features.stopServing', { features: <code>{stopAsk.features.join(', ')}</code>, url: <code>{stopAsk.liveUrl}</code> })
              : tr('features.stopServingNoUrl', { features: <code>{stopAsk.features.join(', ')}</code> })}
          </p>
        )}
        {err && (
          <div className="alert alert-error" role="alert">
            <span>{err.text}</span>
            {err.issues.length > 0 && (
              <ul className="small">
                {err.issues.map((i) => (
                  <li key={`${i.path}:${i.message}`}>
                    <code>{i.path}</code> {i.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </ConfirmDialog>
    </section>
  );
}

/** 手元の kumo.config.json に同じ変更を入れる方法（CLI のコマンド + CLI で入らない行はファイルのどこを直すか） */
function LocalFix({ changes, env }: { changes: Change[]; env: Env }) {
  const { t, tr } = useT();
  const fix = localFix(changes);
  const cmds = [...fix.cli, `${CLI_NAME} config push --env ${env}`];
  return (
    <div className="feature-fix">
      <span>{tr('features.localFix', { file: <code>kumo.config.json</code> })}</span>
      {fix.cli.length > 0 && <CodeBlock code={cmds.join('\n')} />}
      {fix.manual.map((m) => (
        <span key={m.path} className="small">
          {tr('features.localManual', { path: <code>{m.path}</code>, value: <code>{String(m.on)}</code> })}
        </span>
      ))}
      {/* 2026-10-19 creator/ads-open（見た目の確認の指摘）: push のコマンドも上の行と同じくコードの枠に（コピーしやすく・コマンドだと分かるように） */}
      {fix.cli.length === 0 && <span className="small muted">{tr('features.localManualThen', { cmd: <code>{`${CLI_NAME} config push --env ${env}`}</code> })}</span>}
    </div>
  );
}
